/**
 * Questo file definisce il componente PublicRoute.
 * - Agisce come un "guardiano" per le rotte che dovrebbero essere accessibili solo agli utenti non autenticati (es. pagine di login e registrazione).
 * - Se un utente autenticato tenta di accedere a una di queste pagine, viene reindirizzato alla sua dashboard.
 * - Mostra uno spinner di caricamento mentre lo stato di autenticazione viene verificato.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';

/**
 * Componente PublicRoute.
 * - Protegge le rotte pubbliche da accessi di utenti già loggati.
 *
 * @param {object} props - Le props del componente.
 * @param {React.ReactNode} props.children - Il componente React (pagina pubblica) da renderizzare.
 * @returns {React.ReactElement} - Ritorna o il componente figlio, o un reindirizzamento, o un loader.
 */
const PublicRoute = ({ children }) => {
    // Estrae lo stato di autenticazione e di caricamento dallo store Redux.
    const { isAuthenticated, loading } = useSelector((state) => state.auth);

    // Durante la verifica iniziale dello stato di autenticazione, mostra un loader.
    // Questo previene che la pagina di login venga mostrata brevemente per poi sparire se l'utente è già loggato.
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    // Se l'utente è già autenticato, lo reindirizza alla dashboard.
    // Questo impedisce a un utente loggato di vedere nuovamente le pagine di login o registrazione.
    if (isAuthenticated) {
        // Il componente 'Navigate' di React Router esegue il reindirizzamento.
        // 'replace' sostituisce la pagina corrente nella cronologia di navigazione.
        return <Navigate to="/dashboard" replace />;
    }

    // Se l'utente non è autenticato (e il caricamento è finito),
    // mostra il componente figlio, che è la pagina pubblica richiesta (es. Login o Register).
    return children;
};

// Esporta il componente per renderlo disponibile nell'applicazione.
export default PublicRoute;